import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CalculationType } from '@ydistri/api-sdk';
import { MainContent, MainContentWrapper, Section } from '@ydistri/ds';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch } from '../../../store';
import { setSubpage } from '../../../routes/routerSlice';
import { useTemplateOrCalculation } from '../../../hooks/useTemplateOrCalculation';
import { CalculationDetailSubpages } from '../../Calculations/calculationsLib';
import { useGetCalculationQuery } from '../apiCalculationDetail';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import CustomExportRunTable from './components/CustomExportRunTable';
import { useApplicationConfiguration } from '../../../hooks/useApplicationConfiguration';
import { shouldShowCustomExportEntry } from './customExportLib';
import CustomExportButton from '../CustomExportButton';
import { useGetLatestCustomExportRun } from './useGetLatestCustomExportRun';

/**
 * Displays very generic table of customer custom exports - this can be anything from email sending to
 * using customer APIs to send some information about the calculation.
 *
 * One "Run" of custom export can be expanded and have multiple "Items", and every "Item" can be expanded
 * to see its own request and response data.
 */
const CustomExport: React.FC = () => {
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculation();
  const { data: calculationData, isFetching } = useGetCalculationQuery(templateOrCalculation.id);
  const { isCustomExportEnabled } = useApplicationConfiguration();
  const isProduction = calculationData?.type === CalculationType.Production;
  const { data: latestCustomExportRun, isFetching: isLatestCustomExportRunFetching } =
    useGetLatestCustomExportRun(templateOrCalculation, isProduction ? undefined : skipToken);

  useEffect(() => {
    dispatch(setSubpage(CalculationDetailSubpages.CUSTOM_EXPORT));
  }, [dispatch]);

  if (isFetching || (isProduction && isLatestCustomExportRunFetching)) return <LoadingIndicator />;
  if (!calculationData) return <div>Calculation detail is not available.</div>;
  if (calculationData.type !== CalculationType.Production) {
    return <div>Calculation is not set as production, so custom export is not available.</div>;
  }
  if (
    !shouldShowCustomExportEntry({
      isCustomExportEnabled,
      isProduction: true,
      hasRunHistory: !!latestCustomExportRun,
    })
  ) {
    return <div>Custom export is not available for this calculation.</div>;
  }

  return (
    <>
      <Helmet title={`Calculation ${templateOrCalculation.id} - Custom Export`} />
      <MainContentWrapper $flexDirection="column">
        <MainContent>
          <Section header="Custom export" headerActions={<CustomExportButton variant="button" />}>
            <CustomExportRunTable />
          </Section>
        </MainContent>
      </MainContentWrapper>
    </>
  );
};

export default CustomExport;
